import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import IconButton from "../../../../../../components/base/button/icon_button";
import {Divider, Paper, TextField} from "@mui/material";
import {Add, Remove} from "@mui/icons-material";
import {Row} from "reactstrap";
import SearchAutoComplete from "../../../../../../components/base/auto_complete/search_auto_complete";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {TransitionGroup} from "react-transition-group";
import Api from "../../../../../../../core/services/api/api";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes} from "../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import {ShoppingCartDataGridColumnKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import ProductInformationPopover from "../../../../../../components/app-specific/draggable-popover/product-information-popover";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {usePartFormQualificationGate} from "../../../../../../features/part-form-qualification";
import {
    createShoppingCartTarget,
    getShoppingCartPendingKey,
    SuggestiveSellingInlinePending,
    useSuggestiveSelling,
} from "../../../../../../features/suggestive-selling";
import Utils from "../../../../../../../core/services/utils/utils";

const UserShoppingCartViewProductsSection = ({
                                                 cart = {},
                                                 setCart,
                                                 cartLoading,
                                                 userId,
                                                 customerId,
                                             }) => {
    const [updating, setUpdating] = useState(false);
    const [autoCompleteText, setAutoCompleteText] = useState('');
    const [autoCompleteValue, setAutoCompleteValue] = useState(null);
    const [autoCompleteParts, setAutoCompleteParts] = useState([]);
    const [loading, setLoading] = useState(false);
    const searchRef = useRef();
    const isMounted = useIsMounted();
    const {checking: checkingFormQualification, ensureAllQualified, GateDialogs} = usePartFormQualificationGate();
    const {offerAfterAdd} = useSuggestiveSelling();
    const shoppingCartPendingKey = useMemo(() => getShoppingCartPendingKey(userId), [userId]);

    /**
     * Listens for the changes in autoCompleteText and with each change, if the length of the autoComplete text is
     * more than 0, then fetches the parts for the shopping cart.
     */
    useEffect(() => {
        if (!autoCompleteText?.length || autoCompleteText?.length < 3) return;
        const timer = setTimeout(() => {
            if (!isMounted()) return;
            if (!autoCompleteText?.length || autoCompleteText?.length < 3) return;
            getAutoCompleteParts(autoCompleteText)
        }, 500);
        return () => clearTimeout(timer)
    }, [autoCompleteText])


    /**
     * Removes a part from the user cart by calling the api and if the result is successful
     * @param part
     */
    const removePartFromCart = (part) => {
        setUpdating(true);
        const forApi = {
            userId: userId,
            partNumbersRemoveList: [part.partNo],
        }
        Api.updateShoppingCartItems(forApi).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setCart(response.data);
            }
            setUpdating(false);
        })
    }

    /**
     * Verifies form qualification, then adds a part to the user's shopping cart.
     * @param part {any}
     */
    const addPartToCart = useCallback(async (part) => {
        if (!part?.partNumber) {
            return;
        }
        const canProceed = await ensureAllQualified(customerId, [{
            partId: part.id,
            partNumber: part.partNumber,
            partLabel: part.partNumber,
        }]);
        if (!canProceed) {
            return;
        }
        setUpdating(true);
        const forApi = {
            userId: userId,
            PartCountChangedList: [{
                partNo: part.partNumber,
                finalCount: 1,
            }],
        };
        const response = await Api.updateShoppingCartItems(forApi);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            setCart(response.data);
            if (part?.id) {
                await offerAfterAdd({
                    sourcePartId: part.id,
                    sourcePartNumber: part.partNumber,
                    sourceQuantity: 1,
                    pendingKey: shoppingCartPendingKey,
                    target: createShoppingCartTarget({
                        userId,
                        customerId,
                        onCartUpdated: setCart,
                    }),
                });
            }
        }
        if (!isMounted()) {
            return;
        }
        setUpdating(false);
    }, [customerId, ensureAllQualified, isMounted, offerAfterAdd, setCart, shoppingCartPendingKey, userId]);

    /**
     * Fetches the list of parts from the server based on the keywords.
     * @param keywords {string}
     */
    const getAutoCompleteParts = (keywords) => {
        setLoading(true);
        Api.autoCompletePartLookup(keywords).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setAutoCompleteParts(response.data ?? []);
            }
            setLoading(false);
        })
    }

    /**
     * Depending on the type of the part:
     * if string => sets the part to autoCompleteText
     * if object => sets the text to empty string and value to null and adds the part to cart.
     * @param event {MouseEvent}
     * @param part {any}
     */
    const onSelectNewPart = (event, part) => {
        if (!part) part = ''
        if (typeof part === 'string') setAutoCompleteText(part)
        else if (typeof part === 'object') {
            void addPartToCart(part.value);
            setAutoCompleteValue(null);
            setAutoCompleteText('');
        }
    }

    /**
     * Rends the auto complete part with respect to its type
     * @param option
     * @return {JSX.Element|string}
     */
    const renderAutoCompletePart = (props, option) => {
        if (!option) return '';
        if (typeof option === 'string') return (
            <p className='mx-2 p-2'>{option}</p>
        );
        return (
            <Row className='no-gutters align-items-center w-100 justify-content-start py-2'>
                <p className='text-left text-sm text-color-1 mr-5'>
                    {option.value?.partNumber}
                </p>
            </Row>
        )
    }

    /**
     * Gets users confirmation before removing the current product from the shopping cart.
     */
    const getRemoveConfirmation = (part) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this product?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removePartFromCart(part),
        );
    }

    /**
     * Memo version of table columns
     * @type {unknown}
     */
    const dataGridColumns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: ShoppingCartDataGridColumnKeys.remove,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: false,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 0,
                    minWidth: 50,
                }
            },
            {
                name: ShoppingCartDataGridColumnKeys.partImage,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: false,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                    minWidth: 50,
                }
            },
            {
                name: ShoppingCartDataGridColumnKeys.partNumber,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                    minWidth: 50,
                }
            },
            {
                name: ShoppingCartDataGridColumnKeys.partPrice,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.money,
                pinned: false,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 0,
                    minWidth: 100,
                }
            },
            {
                name: ShoppingCartDataGridColumnKeys.partQuantity,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: false,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                    minWidth: 50,
                }
            },
        ], [])

    /**
     * Memo version of data grid rows
     */
    const dataGridRows = useMemo(() => {
        return cart?.parts?.map(part => {
            return ({
                key: part.partNo,
                data: part,
                cells: {
                    [ShoppingCartDataGridColumnKeys.remove]:
                        <IconButton
                            disabled={!!updating}
                            onPressed={() => getRemoveConfirmation(part)}
                            color={'paper.error'}
                        >
                            <RemoveIcon className={'remove-icon'}/>
                        </IconButton>,
                    [ShoppingCartDataGridColumnKeys.partImage]:
                        <div
                            className='cart img row no-gutters align-items-center justify-content-center'>
                            <img
                                src={Utils.encodeFileUrl(part.imageCover)}
                                alt={part.partNo}
                                onContextMenu={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                                className={'disable-downloading-image'}
                            />
                        </div>,
                    [ShoppingCartDataGridColumnKeys.partNumber]:
                        <ProductInformationPopover partNo={part.partNo}>
                            {part.partNo ?? ''}
                        </ProductInformationPopover>,
                    [ShoppingCartDataGridColumnKeys.partPrice]: part?.unitPrice,
                    [ShoppingCartDataGridColumnKeys.partQuantity]:
                        <PartQuantity
                            userId={userId}
                            part={part}
                            updating={!!updating}
                            setCart={setCart}
                        />
                }
            })
        })
    }, [cart?.parts, updating, userId])

    return (
        <>
            {GateDialogs}
            <Paper className='paper w-100 p-3 customer-shopping-cart-view-products'>
                <Row className='no-gutters align-items-center justify-content-between w-100'>
                    <SuggestiveSellingInlinePending
                        pendingKey={shoppingCartPendingKey}
                        sx={{width: '100%', mb: 1}}
                    />
                    <SearchAutoComplete
                        disabled={!!updating || checkingFormQualification}
                        ref={searchRef}
                        value={autoCompleteValue}
                        loading={loading}
                        onSelect={onSelectNewPart}
                        onChange={(e) => setAutoCompleteText(e.target.value)}
                        label={'Search'}
                        clearOnBlur
                        freeSolo={false}
                        type={'text'}
                        inputValue={autoCompleteText}
                        options={autoCompleteParts?.map(part => ({
                            title: `${part.partNumber}`,
                            value: part
                        }))}
                        renderOption={renderAutoCompletePart}
                        loadingText={
                            <div className='loading auto-complete'>
                                <div/>
                                <div/>
                                <div/>
                                <div/>
                            </div>
                        }
                    />
                    <Divider className='divider'/>
                    <div className='my-2 w-100'/>
                    {
                        cartLoading
                            ? (
                                <div className='loading cart-item w-100 my-3'>
                                    <div/>
                                    <div/>
                                    <div/>
                                    <div/>
                                    <div/>
                                </div>
                            )
                            : <TransitionGroup className={'w-100'}>
                                <PartnerDataGrid
                                    columns={dataGridColumns}
                                    rows={dataGridRows}
                                    state={{
                                        loading: {state: loading},
                                    }}
                                    hideFooterActions
                                    hideToolbar
                                    hideFooter
                                />
                            </TransitionGroup>
                    }
                    <div className='my-2 w-100'/>
                </Row>
            </Paper>
        </>
    )
}


const PartQuantity = ({part, updating, setCart, userId}) => {
    const operatorAccessLevels = useAccessLevels();
    const [changing, setChanging] = useState(false);
    const [quantity, setQuantity] = useState(part.quantity ?? 0);
    const isMounted = useIsMounted();

    /**
     * Syncs the quantity state with props.
     */
    useEffect(() => {
        setQuantity(part.quantity ?? 0)
    }, [part?.quantity])

    /**
     * Updates the quantity of a part by calling the api from server, and if successful, updates the ui
     * @type {(function(*=): Promise<void>)}
     */
    const updateQuantity = useCallback(async (quantity) => {
        setChanging(true);
        const forApi = {
            userId: userId,
            PartCountChangedList: [{
                partNo: part.partNo,
                finalCount: quantity,
            }]
        }
        const response = await Api.updateShoppingCartItems(forApi)
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setQuantity(0);
            setCart(response.data);
        }
        setChanging(false);
    }, [isMounted, userId, part.partNo, setCart])

    /**
     * Listens for the changes in the quantity and if the quantity is changed, then updates the quantity of the part
     */
    useEffect(() => {
        if (!quantity || quantity === part.quantity)
            return;
        const parsed = operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(quantity?.toString()) : parseInt(quantity?.toString());
        if (!parsed || parsed === 0) {
            return;
        }
        const timer = setTimeout(() => isMounted() && updateQuantity(quantity), 1000);
        return () => clearTimeout(timer);
    }, [isMounted, quantity, updateQuantity, part.quantity, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])


    /**
     * Callback function to update the quantity of the part
     * @type {(function(*): (void|undefined))|*}
     */
    const onQuantityChange = useCallback((e) => {
        const value = e.target.value;
        if (!value)
            return setQuantity(undefined);
        const parsed = operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(value) : parseInt(value);
        setQuantity(parsed);
    }, [operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])

    return (
        <>
            <div className={'d-flex align-items-center justify-content-center'}>
                <IconButton
                    disabled={quantity === 0 || !!updating || changing}
                    onPressed={() => setQuantity(prevState => Math.max(parseFloat(prevState?.toString()) - 1, 0))}
                    color={'secondary'}
                >
                    <Remove className={'icon small'}/>
                </IconButton>
                <div className={'quantity-field mx-2'}>
                    <TextField
                        size={'small'}
                        fullWidth
                        inputProps={{min: 0, max: 999}}
                        type={'number'}
                        placeholder={''}
                        sx={{
                            '& input': {
                                textAlign: 'center',
                            }
                        }}
                        value={quantity?.toString()}
                        disabled={!!updating || changing}
                        onChange={onQuantityChange}
                    />
                </div>
                <IconButton
                    disabled={!!updating || changing}
                    onPressed={() => setQuantity(prevState => Math.max(parseFloat(prevState?.toString()) + 1, 0))}
                    color={'secondary'}
                >
                    <Add className={'icon small'}/>
                </IconButton>
            </div>
        </>
    )
}

export default UserShoppingCartViewProductsSection;
